import { arrange, DASHBOARD_SETTING, type Arrangement } from '@nexus/core';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  IconButton,
  Switch,
  toast,
} from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminSettingsQuery } from '../admin/api';
import { useCan } from '../session/session';
import { useSettings, useUpdatePersonalSetting } from '../session/settings';
import { useSaveAdminSetting } from '../settings/setting-rows';
import type { DashboardTile } from './tiles';

interface Row {
  tile: DashboardTile;
  shown: boolean;
}

/** One list in the stored order, hidden tiles at their place. */
function rowsOf(tiles: readonly DashboardTile[], layout: Arrangement): Row[] {
  return arrange(tiles, { order: layout.order, hidden: [] }).visible.map((tile) => ({
    tile,
    shown: !layout.hidden.includes(tile.key),
  }));
}

function toArrangement(rows: readonly Row[]): Arrangement {
  return {
    order: rows.map((row) => row.tile.key),
    hidden: rows.filter((row) => !row.shown).map((row) => row.tile.key),
  };
}

/**
 * "Anpassen" on the start page: show, hide and move tiles. Saved as the personal setting
 * `core.dashboard`; people with `company.settings.manage` can save it as the company default.
 * Moving works with buttons, so it needs no dragging (WCAG 2.5.7).
 */
export function CustomizeDashboard({
  tiles,
  layout,
}: {
  tiles: readonly DashboardTile[];
  layout: Arrangement;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const settings = useSettings();
  const updatePersonal = useUpdatePersonalSetting();
  const saveAdmin = useSaveAdminSetting();
  const canSetDefault = useCan('company.settings.manage');
  const adminValues = useQuery({ ...adminSettingsQuery, enabled: open && canSetDefault });
  const personal = settings.personal.includes(DASHBOARD_SETTING);
  const companyDefault = adminValues.data?.[DASHBOARD_SETTING] !== undefined;

  function onOpenChange(next: boolean) {
    if (next) setRows(rowsOf(tiles, layout));
    setOpen(next);
  }

  function move(index: number, by: -1 | 1) {
    setRows((current) => {
      const next = [...current];
      const [row] = next.splice(index, 1);
      next.splice(index + by, 0, row!);
      return next;
    });
  }

  function savePersonal(value: Arrangement | null) {
    updatePersonal.mutate(
      { key: DASHBOARD_SETTING, value },
      { onError: () => toast.error(t('common.saveFailed')) },
    );
    setOpen(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button icon="sliders-horizontal" iconOnlyOnPhone>
          {t('home.customize')}
        </Button>
      </DialogTrigger>
      <DialogContent
        title={t('home.customizeTitle')}
        description={t('home.customizeDescription')}
        footer={
          <>
            {personal && (
              <Button variant="ghost" icon="rotate-ccw" onClick={() => savePersonal(null)}>
                {t('home.restoreDefault')}
              </Button>
            )}
            <Button variant="primary" onClick={() => savePersonal(toArrangement(rows))}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <ul className="flex flex-col divide-y divide-line">
          {rows.map((row, index) => {
            const title = t(row.tile.title);
            const id = `dashboard-tile-${row.tile.key}`;
            return (
              <li key={row.tile.key} className="flex items-center gap-3 py-2.5">
                <Icon
                  name={row.tile.icon}
                  className={row.shown ? 'text-fg-muted' : 'text-fg-disabled'}
                />
                <div className="min-w-0 flex-1">
                  <label htmlFor={id} className="block truncate font-medium text-fg">
                    {title}
                  </label>
                  <p className="truncate text-xs text-fg-subtle">
                    {row.tile.source ? t(row.tile.source) : t('home.general')}
                    {row.tile.description && ` · ${t(row.tile.description)}`}
                  </p>
                </div>
                <IconButton
                  icon="arrow-up"
                  size="sm"
                  label={t('home.moveUp', { title })}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                />
                <IconButton
                  icon="arrow-down"
                  size="sm"
                  label={t('home.moveDown', { title })}
                  disabled={index === rows.length - 1}
                  onClick={() => move(index, 1)}
                />
                <Switch
                  id={id}
                  checked={row.shown}
                  onCheckedChange={(shown) =>
                    setRows((current) =>
                      current.map((r) => (r.tile.key === row.tile.key ? { ...r, shown } : r)),
                    )
                  }
                />
              </li>
            );
          })}
        </ul>
        {canSetDefault && (
          <div className="mt-4 flex flex-col gap-2 rounded-md border border-line bg-canvas p-3">
            <p className="text-sm text-fg-muted">{t('home.companyDefaultHint')}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                icon="building-2"
                loading={saveAdmin.isPending}
                onClick={() =>
                  saveAdmin.mutate({ key: DASHBOARD_SETTING, value: toArrangement(rows) })
                }
              >
                {t('home.saveCompanyDefault')}
              </Button>
              {companyDefault && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon="rotate-ccw"
                  disabled={saveAdmin.isPending}
                  onClick={() => saveAdmin.mutate({ key: DASHBOARD_SETTING, value: null })}
                >
                  {t('home.removeCompanyDefault')}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
